import { computed, onUnmounted, ref, watch, type Ref } from 'vue'

type Translate = (key: string) => string

export const useSidebarPoints = ({
  t,
  assetPoints,
  assetLoading,
  assetIsVip,
  assetVipExpireAt,
}: {
  t: Translate
  assetPoints: Ref<number>
  assetLoading: Ref<boolean>
  assetIsVip: Ref<boolean>
  assetVipExpireAt: Ref<string | null>
}) => {
  const compactFormatter = new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
  const fullFormatter = new Intl.NumberFormat()

  const animatedPointsRaw = ref(0)
  let pointsAnimationFrameId: number | null = null

  const animateValue = (from: number, to: number, duration: number, callback: (v: number) => void) => {
    if (pointsAnimationFrameId !== null) {
      cancelAnimationFrame(pointsAnimationFrameId)
      pointsAnimationFrameId = null
    }
    if (from === to) {
      callback(to)
      return
    }

    const start = performance.now()
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      callback(Math.round(from + (to - from) * eased))

      if (progress < 1) {
        pointsAnimationFrameId = requestAnimationFrame(step)
        return
      }
      pointsAnimationFrameId = null
    }

    pointsAnimationFrameId = requestAnimationFrame(step)
  }

  watch(
    assetPoints,
    (val) => {
      animateValue(animatedPointsRaw.value, Number(val || 0), 800, (v) => {
        animatedPointsRaw.value = v
      })
    },
    { immediate: true },
  )

  const pointsDisplay = computed(() => fullFormatter.format(animatedPointsRaw.value))
  const compactPoints = computed(() => compactFormatter.format(animatedPointsRaw.value))
  const isPointsLow = computed(() => Number(assetPoints.value || 0) <= 20)
  const collapsedPointsTooltip = computed(() => {
    const base = `${t('workbench.contactPoints')}: ${pointsDisplay.value}`
    return isPointsLow.value ? `${base} · ${t('workbench.contactPointsLow')}` : base
  })

  const vipExpireMeta = computed(() => {
    if (!assetIsVip.value || !assetVipExpireAt.value) {
      return { valid: false, dateText: '-', daysRemaining: null as number | null }
    }
    const expireDate = new Date(assetVipExpireAt.value)
    if (Number.isNaN(expireDate.getTime())) {
      return { valid: false, dateText: '-', daysRemaining: null as number | null }
    }

    const y = expireDate.getFullYear()
    const m = String(expireDate.getMonth() + 1).padStart(2, '0')
    const day = String(expireDate.getDate()).padStart(2, '0')
    const dateText = `${y}-${m}-${day}`

    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const endOfExpire = new Date(
      expireDate.getFullYear(),
      expireDate.getMonth(),
      expireDate.getDate(),
    )
    const diffMs = endOfExpire.getTime() - startOfToday.getTime()
    const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000))
    const daysRemaining = Math.max(0, days)

    return { valid: true, dateText, daysRemaining }
  })

  const vipExpireDateText = computed(() => vipExpireMeta.value.dateText)
  const vipLabelText = computed(() =>
    assetIsVip.value ? t('profile.vipExpireAt') : t('profile.vipStatus'),
  )
  const vipStatusText = computed(() => {
    if (assetLoading.value) return '...'
    return vipExpireMeta.value.valid ? vipExpireDateText.value : t('profile.memberRegular')
  })
  const vipStatusTooltip = computed(() => {
    if (assetLoading.value) return `${vipLabelText.value}: ...`
    return `${vipLabelText.value}: ${vipStatusText.value}`
  })
  const vipExpireState = computed(() => {
    const { daysRemaining } = vipExpireMeta.value
    if (daysRemaining === null) return 'neutral'
    if (daysRemaining <= 7) return 'urgent'
    if (daysRemaining <= 30) return 'warning'
    return 'healthy'
  })

  onUnmounted(() => {
    if (pointsAnimationFrameId !== null) {
      cancelAnimationFrame(pointsAnimationFrameId)
      pointsAnimationFrameId = null
    }
  })

  return {
    pointsDisplay,
    compactPoints,
    collapsedPointsTooltip,
    isPointsLow,
    vipLabelText,
    vipStatusText,
    vipStatusTooltip,
    vipExpireState,
  }
}
